import { CTAButton } from "../components/CTAButton";
import Layout from "../components/Layout";
import { cn } from "../lib/utils";
import { Link, useNavigate } from "@tanstack/react-router";
import { CalendarDays, Clock, Info } from "lucide-react";
import { motion } from "motion/react";

// ─── Schedule Data ─────────────────────────────────────────────────────────────

interface ClassEntry {
  time: string;
  name: string;
  duration: string;
  isAdvanced?: boolean;
}

interface DaySchedule {
  day: string;
  fullDay: string;
  classes: ClassEntry[];
}

const scheduleData: DaySchedule[] = [
  {
    day: "Mon",
    fullDay: "Monday",
    classes: [
      { time: "8:30 AM", name: "10P Jiu Jitsu", duration: "8:30–10:00 AM" },
      { time: "12:00 PM", name: "Muay Thai", duration: "12:00–1:00 PM" },
      { time: "12:00 PM", name: "Open Mat", duration: "12:00–1:00 PM" },
      { time: "6:00 PM", name: "10P Jiu Jitsu", duration: "6:00–7:00 PM" },
      { time: "6:00 PM", name: "Muay Thai", duration: "6:00–7:00 PM" },
      {
        time: "7:00 PM",
        name: "Comp Class",
        duration: "7:00–8:00 PM",
        isAdvanced: true,
      },
      { time: "8:00 PM", name: "MMA", duration: "8:00–9:00 PM" },
    ],
  },
  {
    day: "Tue",
    fullDay: "Tuesday",
    classes: [
      { time: "5:00 AM", name: "10P Jiu Jitsu", duration: "5:00–6:00 AM" },
      { time: "11:30 AM", name: "10P Jiu Jitsu", duration: "11:30 AM–1:00 PM" },
      { time: "5:00 PM", name: "Adult Wrestling", duration: "5:00–6:00 PM" },
      { time: "6:00 PM", name: "10P Jiu Jitsu", duration: "6:00–7:00 PM" },
      { time: "6:00 PM", name: "Muay Thai", duration: "6:00–7:00 PM" },
      {
        time: "7:00 PM",
        name: "Advanced Jiu Jitsu",
        duration: "7:00–8:00 PM",
        isAdvanced: true,
      },
      { time: "7:00 PM", name: "Boxing", duration: "7:00–8:00 PM" },
    ],
  },
  {
    day: "Wed",
    fullDay: "Wednesday",
    classes: [
      { time: "5:00 AM", name: "10P Jiu Jitsu", duration: "5:00–6:00 AM" },
      { time: "8:30 AM", name: "10P Jiu Jitsu", duration: "8:30–10:00 AM" },
      { time: "12:00 PM", name: "Muay Thai", duration: "12:00–1:00 PM" },
      { time: "12:00 PM", name: "Open Mat", duration: "12:00–1:00 PM" },
      { time: "6:00 PM", name: "10P Jiu Jitsu", duration: "6:00–7:00 PM" },
      { time: "6:00 PM", name: "Muay Thai", duration: "6:00–7:00 PM" },
      {
        time: "7:00 PM",
        name: "Advanced Jiu Jitsu",
        duration: "7:00–8:00 PM",
        isAdvanced: true,
      },
      { time: "8:00 PM", name: "MMA", duration: "8:00–9:00 PM" },
    ],
  },
  {
    day: "Thu",
    fullDay: "Thursday",
    classes: [
      { time: "5:00 AM", name: "10P Jiu Jitsu", duration: "5:00–6:00 AM" },
      { time: "11:30 AM", name: "10P Jiu Jitsu", duration: "11:30 AM–1:00 PM" },
      { time: "5:00 PM", name: "Adult Wrestling", duration: "5:00–6:00 PM" },
      { time: "6:00 PM", name: "10P Jiu Jitsu", duration: "6:00–7:00 PM" },
      { time: "6:00 PM", name: "Muay Thai", duration: "6:00–7:00 PM" },
      {
        time: "7:00 PM",
        name: "Advanced Jiu Jitsu",
        duration: "7:00–8:00 PM",
        isAdvanced: true,
      },
      { time: "7:00 PM", name: "Boxing", duration: "7:00–8:00 PM" },
    ],
  },
  {
    day: "Fri",
    fullDay: "Friday",
    classes: [
      { time: "8:30 AM", name: "Task Based Games", duration: "8:30–10:00 AM" },
      { time: "12:00 PM", name: "10P Jiu Jitsu", duration: "12:00–1:00 PM" },
      { time: "5:00 PM", name: "Muay Thai", duration: "5:00–6:00 PM" },
      { time: "5:00 PM", name: "Basics Jiu Jitsu", duration: "5:00–6:00 PM" },
      { time: "6:00 PM", name: "Open Mat", duration: "6:00–8:00 PM" },
      { time: "8:00 PM", name: "MMA", duration: "8:00–9:00 PM" },
    ],
  },
  {
    day: "Sat",
    fullDay: "Saturday",
    classes: [
      {
        time: "10:00 AM",
        name: "Leg Locks",
        duration: "10:00–11:30 AM",
        isAdvanced: true,
      },
    ],
  },
];

// ─── Color mapping by class type ───────────────────────────────────────────────

function getClassStyle(name: string): string {
  const lower = name.toLowerCase();
  if (
    lower.includes("jiu jitsu") ||
    lower.includes("comp class") ||
    lower.includes("leg locks")
  ) {
    return "bg-primary/15 border-primary/30 text-primary";
  }
  if (
    lower.includes("muay thai") ||
    lower.includes("boxing") ||
    lower.includes("mma")
  ) {
    return "bg-accent/15 border-accent/30 text-accent-foreground";
  }
  if (lower.includes("wrestling")) {
    return "bg-secondary border-border text-secondary-foreground";
  }
  if (lower.includes("open mat")) {
    return "bg-muted border-border text-foreground";
  }
  return "bg-card border-border text-foreground";
}

// ─── Day Card ──────────────────────────────────────────────────────────────────

function DayCard({ day, index }: { day: DaySchedule; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.08 }}
      className="bg-card rounded-2xl shadow-card border border-border overflow-hidden flex flex-col"
    >
      {/* Day header */}
      <div className="bg-primary px-5 py-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
          <CalendarDays className="w-4 h-4 text-foreground" />
        </div>
        <div>
          <div className="font-display font-black text-base text-primary-foreground tracking-tight">
            {day.fullDay}
          </div>
          <div className="text-xs text-primary-foreground/60 font-body">
            {day.classes.length}{" "}
            {day.classes.length === 1 ? "class" : "classes"}
          </div>
        </div>
      </div>

      {/* Classes list */}
      <div className="flex-1 p-4 flex flex-col gap-2">
        {day.classes.map((cls, i) => (
          <div
            key={`${cls.time}-${cls.name}-${i}`}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 border text-sm font-body",
              getClassStyle(cls.name),
            )}
          >
            <Clock className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold leading-snug truncate">
                {cls.name}
                {cls.isAdvanced && (
                  <span className="ml-1.5 text-[10px] font-body font-bold uppercase tracking-wide opacity-70">
                    Advanced
                  </span>
                )}
              </div>
              <div className="text-[11px] opacity-60 font-body">
                {cls.duration}
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function SchedulePage() {
  const navigate = useNavigate();
  return (
    <Layout>
      {/* SEO title */}
      <title>Class Schedule | Superhero Jiu Jitsu Academy</title>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section
        className="relative bg-hero overflow-hidden py-16 md:py-24"
        aria-labelledby="schedule-hero-heading"
      >
        <div className="absolute inset-0 opacity-5" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, var(--color-accent) 0, var(--color-accent) 1px, transparent 0, transparent 50%)",
              backgroundSize: "20px 20px",
            }}
          />
        </div>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="max-w-3xl mx-auto"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
              Springfield's Premier Martial Arts
            </span>
            <h1
              id="schedule-hero-heading"
              className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight text-balance mb-5"
            >
              Superhero Jiu Jitsu Academy Class Schedule
            </h1>
            <p className="text-hero-foreground text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8 opacity-80">
              Monday through Saturday — find the class that fits your life. All
              levels welcome.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <CTAButton
                variant="hero"
                size="lg"
                data-ocid="schedule-hero-cta"
                onClick={() => navigate({ to: "/contact-us" })}
              >
                Claim Your Free Class
              </CTAButton>
              <Link
                to="/contact-us"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gold/50 px-6 py-3 text-sm font-display font-bold text-hero-foreground uppercase tracking-wide hover:bg-gold/10 transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-ocid="schedule-hero-contact"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── NOTE BANNER ──────────────────────────────────────────────────── */}
      <section
        className="bg-card border-y border-border"
        aria-label="Schedule note"
      >
        <div className="container mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-center gap-2 text-sm font-body text-muted-foreground">
            <Info className="w-4 h-4 text-gold flex-shrink-0" />
            <span>
              All classes are beginner-friendly unless marked{" "}
              <strong className="font-semibold text-foreground">
                Advanced
              </strong>
            </span>
          </div>
        </div>
      </section>

      {/* ── SCHEDULE GRID ────────────────────────────────────────────────── */}
      <section
        className="bg-background py-16 md:py-20"
        aria-labelledby="schedule-grid-heading"
      >
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-3">
              Weekly Classes
            </span>
            <h2
              id="schedule-grid-heading"
              className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance"
            >
              Monday – Saturday
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {scheduleData.map((day, i) => (
              <DayCard key={day.day} day={day} index={i} />
            ))}
          </div>

          {/* Legend */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="mt-10 flex flex-wrap gap-3 justify-center"
          >
            {[
              {
                label: "Jiu Jitsu / Grappling",
                cls: "bg-primary/15 border-primary/30",
              },
              {
                label: "Striking / MMA / Boxing",
                cls: "bg-accent/15 border-accent/30",
              },
              { label: "Wrestling", cls: "bg-secondary border-border" },
              { label: "Open Mat / Other", cls: "bg-muted border-border" },
            ].map(({ label, cls }) => (
              <div key={label} className="flex items-center gap-1.5">
                <span
                  className={cn("w-3 h-3 rounded border flex-shrink-0", cls)}
                />
                <span className="text-xs font-body text-muted-foreground">
                  {label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CTA SECTION ──────────────────────────────────────────────────── */}
      <section
        className="bg-hero relative overflow-hidden py-20"
        aria-labelledby="schedule-cta-heading"
      >
        <div className="absolute inset-0 opacity-5" aria-hidden="true">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, var(--color-accent) 0, var(--color-accent) 1px, transparent 0, transparent 50%)",
              backgroundSize: "20px 20px",
            }}
          />
        </div>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto"
          >
            <span className="inline-block text-xs font-body font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
              Limited Spots Available
            </span>
            <h2
              id="schedule-cta-heading"
              className="font-display font-black text-4xl sm:text-5xl text-hero-foreground mb-4 text-balance"
            >
              Start Your Jiu Jitsu Journey Today
            </h2>
            <p className="text-hero-foreground text-lg mb-8 leading-relaxed opacity-80">
              Your first class is completely free — no experience needed, no
              commitment required. Come see what Superhero Jiu Jitsu Academy is
              all about.
            </p>
            <Link
              to="/contact-us"
              data-ocid="schedule-bottom-cta"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-8 py-4 text-base font-display font-black uppercase tracking-widest text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Claim Your Free Class
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
